import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { ToastAction, ToastPlacement } from '@/shared/ui/feedback/types'
import type { UiTone } from '@/shared/ui/types'

// The single cross-cutting UI store (contract 02 §7): theme (persisted user pref),
// direction (runtime-only — derived from the active locale) and the transient toast
// queue / active confirm. Modal open-state stays local to consumers — no global
// "modal manager".

export type ThemePreference = 'light' | 'dark' | 'system'
export type Direction = 'ltr' | 'rtl'

export interface ToastRecord {
  id: number
  message: string
  title?: string
  tone: UiTone
  timeout: number
  progress: boolean
  action?: ToastAction
  placement: ToastPlacement
  dismissible: boolean
  /** Changes whenever content/timing changes so the host can restart its timer deterministically. */
  revision: number
}

export interface ConfirmRequest {
  id: number
  message: string
  title?: string
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
  resolve: (confirmed: boolean) => void
}

let nextId = 0
export const MAX_ACTIVE_TOASTS = 5

type NewToast = Omit<ToastRecord, 'id' | 'revision'>
type ToastPatch = Partial<NewToast>

function actionKey(action: ToastAction | undefined): string {
  if (!action) return ''
  const target = typeof action.to === 'string' ? action.to : JSON.stringify(action.to)
  return `${action.label}:${target}`
}

function toastKey(toast: NewToast | ToastRecord): string {
  return [
    toast.title ?? '',
    toast.message,
    toast.tone,
    toast.progress ? 'progress' : 'settled',
    toast.placement,
    toast.dismissible ? 'dismissible' : 'locked',
    actionKey(toast.action),
  ].join('\u0000')
}

export const useUiStore = defineStore(
  'ui',
  () => {
    const theme = ref<ThemePreference>('light')
    const direction = ref<Direction>('ltr')
    const toasts = ref<ToastRecord[]>([])
    const confirmQueue = ref<ConfirmRequest[]>([])
    const confirm = computed(() => confirmQueue.value[0] ?? null)
    let resolvingConfirm = false

    function setTheme(value: ThemePreference): void {
      theme.value = value
    }
    // Direction is owned by the locale: only the app shell's locale watcher may call this,
    // with localeMeta[locale].dir. It is never a direct user preference.
    function setDirection(value: Direction): void {
      direction.value = value
    }

    function pushToast(toast: NewToast): number {
      const duplicate = toasts.value.find((active) => toastKey(active) === toastKey(toast))
      if (duplicate) {
        updateToast(duplicate.id, toast)
        return duplicate.id
      }

      const id = ++nextId
      toasts.value.push({ ...toast, id, revision: 0 })
      if (toasts.value.length > MAX_ACTIVE_TOASTS) {
        toasts.value.splice(0, toasts.value.length - MAX_ACTIVE_TOASTS)
      }
      return id
    }

    function updateToast(id: number, patch: ToastPatch): void {
      const index = toasts.value.findIndex((toast) => toast.id === id)
      if (index < 0) return
      const current = toasts.value[index]
      if (!current) return

      const progress = patch.progress ?? current.progress
      const next: ToastRecord = {
        ...current,
        ...patch,
        progress,
        timeout: progress ? 0 : (patch.timeout ?? current.timeout),
        revision: current.revision + 1,
      }
      toasts.value.splice(index, 1, next)
    }

    function dismissToast(id: number): void {
      toasts.value = toasts.value.filter((t) => t.id !== id)
    }

    function requestConfirm(request: Omit<ConfirmRequest, 'id' | 'resolve'>): Promise<boolean> {
      return new Promise<boolean>((resolve) => {
        confirmQueue.value.push({ ...request, id: ++nextId, resolve })
      })
    }
    function resolveConfirm(confirmed: boolean): void {
      // Headless close paths can emit more than once in one turn. Do not let a duplicate close
      // settle the next queued request.
      if (resolvingConfirm) return
      const current = confirmQueue.value.shift()
      if (!current) return
      resolvingConfirm = true
      current.resolve(confirmed)
      queueMicrotask(() => {
        resolvingConfirm = false
      })
    }

    function resetTransientUi(): void {
      toasts.value = []
      const pending = confirmQueue.value.splice(0)
      for (const request of pending) request.resolve(false)
      resolvingConfirm = false
    }

    return {
      theme,
      direction,
      toasts,
      confirm,
      setTheme,
      setDirection,
      pushToast,
      updateToast,
      dismissToast,
      requestConfirm,
      resolveConfirm,
      resetTransientUi,
    }
  },
  // Only durable user preferences persist; toasts/confirm are transient. Direction is
  // deliberately NOT persisted — it re-derives from the locale on every boot, so a saved
  // 'rtl' can never outlive a switch to an LTR locale (ORP-18591 / analysis 05 §7#2).
  { persist: { pick: ['theme'] } },
)
